import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Heart } from 'lucide-react';

const getImageUrl = (product) => {
  if (!product) return 'https://via.placeholder.com/300x200?text=No+Image';
  // Multi-image: use primary image from product_images table
  if (product.primary_image_id) {
    return `${api.getApiUrl()}/api/product-images/db/${product.primary_image_id}`;
  }
  // Legacy DB-stored image (fallback via product ID)
  if (product.has_db_image) {
    return `${api.getApiUrl()}/api/product-images/db/product/${product.id}`;
  }
  const url = product.image_url;
  if (!url) return 'https://via.placeholder.com/300x200?text=No+Image';
  if (url.startsWith('http')) return url;
  return `${api.getApiUrl()}/api/product-images${encodeURI(url)}`;
};

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Check if product is in wishlist on mount
  useEffect(() => {
    if (isAuthenticated) {
      checkWishlistStatus();
    }
  }, [isAuthenticated, product.id]);

  const checkWishlistStatus = async () => {
    try {
      const data = await api.checkWishlist(product.id);
      setIsWishlisted(data.inWishlist);
    } catch (error) {
      // Silently fail - not critical
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(product.id, 1);
    if (result.success) {
      alert('Added to cart!');
    } else {
      alert(result.error || 'Failed to add to cart');
    }
  };

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Navigate to checkout with just this product
    navigate('/checkout', {
      state: {
        buyNow: true,
        item: {
          product_id: product.id,
          name: product.name,
          price: parseFloat(product.price),
          quantity: 1,
          image_url: product.image_url,
          weight: parseFloat(product.weight) || 0
        }
      }
    });
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (wishlistLoading) return;

    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await api.removeFromWishlist(product.id);
        setIsWishlisted(false);
      } else {
        await api.addToWishlist(product.id);
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    } finally {
      setWishlistLoading(false);
    }
  };

  const isNew = product.id <= 5;
  const isMemberPrice = product.isMember && product.originalPrice;
  const isOutOfStock = product.stock === 0 || product.stock === '0';

  return (
    <div className="product-card">
      {/* Badge */}
      {isOutOfStock && <span className="product-badge" style={{ background: '#95A5A6' }}>OUT OF STOCK</span>}
      {!isOutOfStock && isMemberPrice && <span className="product-badge" style={{ background: '#FF7B54' }}>MEMBER</span>}
      {!isOutOfStock && isNew && !isMemberPrice && <span className="product-badge" style={{ background: '#00BFB3' }}>NEW</span>}

      {/* Wishlist Button */}
      <button
        className="product-wishlist"
        onClick={handleWishlist}
        disabled={wishlistLoading}
        style={{ color: isWishlisted ? '#FF7B54' : '#B2BEC3' }}
      >
        {isWishlisted ? <Heart size={20} strokeWidth={1.5} fill="currentColor" /> : <Heart size={20} strokeWidth={1.5} />}
      </button>

      <Link to={`/product/${product.id}`}>
        {/* Product Image */}
        <div className="product-image-wrapper" style={{ position: 'relative' }}>
          <img
            src={getImageUrl(product)}
            alt={product.name}
            className="product-image"
            style={isOutOfStock ? { filter: 'blur(2px) grayscale(40%)', opacity: 0.6 } : {}}
          />
          {isOutOfStock && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '700',
                letterSpacing: '1px'
              }}>
                OUT OF STOCK
              </span>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="product-info">
          <span className="product-category">{product.category}</span>
          <h3 className="product-name">{product.name}</h3>

          {/* Price */}
          <div className="product-price-row">
            <span className="product-price">RM {parseFloat(product.price).toFixed(2)}</span>
            {isMemberPrice && (
              <span className="product-price-old">
                RM {parseFloat(product.originalPrice).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Action Buttons - Outside Link for better click handling */}
      <div className="product-actions">
        <button
          onClick={handleAddToCart}
          className="product-btn btn-cart"
          disabled={product.stock === 0}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <span>Add to Cart</span>
        </button>
        <button
          onClick={handleBuyNow}
          className="product-btn btn-buy"
          disabled={product.stock === 0}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
          <span>Buy Now</span>
        </button>
      </div>

      <style>{`
        .product-card .product-info {
          padding-bottom: 0 !important;
        }

        .product-actions {
          display: flex;
          gap: 8px;
          padding: 10px 18px 18px;
          margin-top: auto;
        }

        .product-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 12px;
          border: none;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .product-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .product-btn svg {
          flex-shrink: 0;
        }

        .btn-cart {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }

        .btn-cart:hover:not(:disabled) {
          background: #e0e0e0;
          border-color: #ccc;
        }

        .btn-buy {
          background: linear-gradient(135deg, #E891A8 0%, #D97E96 100%);
          color: #fff;
        }

        .btn-buy:hover:not(:disabled) {
          background: linear-gradient(135deg, #D97E96 0%, #CC6D86 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(232, 145, 168, 0.4);
        }

        .btn-buy:active:not(:disabled) {
          transform: translateY(0);
        }

        @media (max-width: 480px) {
          .product-actions {
            flex-direction: column;
            gap: 6px;
          }

          .product-btn {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCard;
